import MainThemeButton from '@components/MainThemeButton';
import useCommon from '@utils/hooks/common';
import classNames from 'classnames';
import {Form, Select, Statistic} from 'antd';
import {CloseOutlined} from '@ant-design/icons';
import React, {
  memo,
  forwardRef,
  useEffect,
  useState,
  useImperativeHandle,
  CSSProperties,
  CSSProperties,
} from 'react';
import useRequest from '@utils/hooks/useRequestCustom';
import {getGameEventV3, ROOM_ACTION} from '@constant/game';
import gameApi from '@utils/api/game.api';
import styles from './styles';
import GradientButton from '@components/GradientButton';
import Countdown, {zeroPad} from 'react-countdown';
import dayjs from 'dayjs';

interface ExtendTimerInterface {
  expiredAt?: number;
  roomNumber?: string;
  encryptionKey?: any;
  visible?: boolean;
}

const ExtendTimer = forwardRef((props: ExtendTimerInterface, ref) => {
  const {expiredAt, roomNumber, encryptionKey, visible} = props;

  const {classes, socket, t, user} = useCommon({
    withStyles: styles,
    observers: ['socket', 'user'],
    withTranslation: ['room_setting_drawer'],
  });

  const {runAsync: getRoomPriceData, data: roomPriceData} = useRequest(gameApi.getRoomPrice, {
    manual: true,
    initialData: [],
  });

  const INITIAL_VALUES = {
    GAME_DURATION: roomPriceData && roomPriceData.length > 0 ? roomPriceData[0]?.lengthMin : 0,
  };

  const [isTimerTextVisbile, setIsTimerTextVisbile] = useState<boolean>(true);
  const [isTimerDrawerVisbile, setIsTimerDrawerVisbile] = useState<boolean>(false);
  const [diamondAmount, setDiamondAmount] = useState<number>(null);
  const [gameDuration, setGameDuration] = useState<number>(INITIAL_VALUES.GAME_DURATION);

  const {useForm} = Form;
  const [form] = useForm();
  const GAME_EVENTS_V3 = getGameEventV3();
  const {Option} = Select;

  const diamondBalance = user.getDiamondWalletBalance();

  useImperativeHandle(ref, () => ({
    showDrawer: () => {
      setIsTimerDrawerVisbile(true);
      setIsTimerTextVisbile(false);
    },
  }));

  const FORM_FIELDS = {
    TIME_ID: 'timeId',
  };

  const getInitialValues = async () => {
    const result = await getRoomPriceData();
    if (result) {
      setGameDuration(result[0]?.lengthMin);
      setDiamondAmount(result[0]?.diamond);
      form.setFieldValue(FORM_FIELDS.TIME_ID, result[0]?.id);
    }
  };

  useEffect(() => {
    getInitialValues();
  }, [visible]);

  const handleFinish = async value => {
    const data = {priceId: value[FORM_FIELDS.TIME_ID], action: ROOM_ACTION.PRIVATE_ROOM_ADD_FUND};

    try {
      socket.call(GAME_EVENTS_V3.EM_PLAYER_SET_ROOM_ACTION, {
        roomCode: roomNumber,
        data,
      });
      setTimeout(() => {
        setIsTimerTextVisbile(true);
      }, 800);
      setIsTimerDrawerVisbile(false);
    } catch {
      console.log('extend expried time failed');
    }
  };

  const renderer = ({minutes, seconds, valueStyles}) => {
    return <span style={valueStyles}>{`${zeroPad(minutes)}:${zeroPad(seconds)}`}</span>;
  };

  const renderExpiredTimer = (props: {
    timeLimitLocal: any;
    valueStyles: CSSProperties;
  }): JSX.Element => {
    const {timeLimitLocal, valueStyles} = props;
    return (
      <Countdown
        date={dayjs(timeLimitLocal).toDate()}
        renderer={({minutes, seconds}) => renderer({minutes, seconds, valueStyles})}
      />
    );
    // return (
    //   <Statistic.Countdown
    //     valueStyle={{
    //       color: 'white',
    //       fontSize,
    //       fontFamily: 'Avenir Next Condensed',
    //       fontWeight: 'bold',
    //     }}
    //     value={timeLimitLocal}
    //     format="mm:ss"
    //   />
    // );
  };

  return (
    <>
      <div
        className={classNames(classes.extendTimerText, !isTimerTextVisbile && classes.hidden)}
        onClick={() => {
          setIsTimerTextVisbile(false);
          setIsTimerDrawerVisbile(true);
        }}
      >
        <div>{t('recharge')}</div>
        {renderExpiredTimer({
          timeLimitLocal: expiredAt,
          valueStyles: {
            color: 'white',
            fontSize: 15,
            fontFamily: 'Avenir Next Condensed',
            fontWeight: 'bold',
          },
        })}
      </div>
      <div
        className={classNames(
          classes.extendTimerContainer,
          !isTimerDrawerVisbile && classes.hidden
        )}
      >
        <div
          className={classes.iconButton}
          onClick={() => {
            setIsTimerTextVisbile(true);
            setIsTimerDrawerVisbile(false);
          }}
        >
          <CloseOutlined />
        </div>
        <Form
          initialValues={{
            gameDuration: INITIAL_VALUES.GAME_DURATION,
          }}
          form={form}
          className={classes.antdForm}
          onValuesChange={changedValue => {
            const [key, value] = Object.entries(changedValue)[0];
            if (key === FORM_FIELDS.TIME_ID) {
              const roomPriceId = value as number;
              const selectedOption = roomPriceData?.find(room => room.id === roomPriceId);

              if (!selectedOption) return;
              const {diamond, lengthMin} = selectedOption;
              setGameDuration(lengthMin);
              setDiamondAmount(diamond);
            }
          }}
          onFinish={handleFinish}
        >
          <div className={classes.container}>
            <div className={classes.titleContainer}>{t('remaining_room_time')}</div>
            {renderExpiredTimer({
              timeLimitLocal: expiredAt,
              valueStyles: {
                color: 'white',
                fontSize: 32,
                fontFamily: 'Avenir Next Condensed',
                fontWeight: 'bold',
              },
            })}
            <div className={classes.timeRow}>
              {t('time_increase')}
              <span className={classes.value}>{t('minute', {value: gameDuration})}</span>
            </div>
            <div className={classes.diamondRow}>
              {t('diamond_require')}
              <span
                className={diamondBalance < diamondAmount ? classes.warning : classes.diamondNeeded}
              >
                {diamondAmount}
              </span>
            </div>

            <div
              className={
                diamondBalance < diamondAmount ? classes.messageRow : classes.messageRowHidden
              }
            >
              <span>{t('diamond_not_enough')}</span>
            </div>

            <Form.Item name={FORM_FIELDS.TIME_ID}>
              <Select
                bordered={false}
                className={classes.customSelect}
                popupClassName={classes.customDropdown}
                placement="topRight"
              >
                {roomPriceData
                  ?.sort((a, b) => {
                    return a.lengthMin - b.lengthMin;
                  })
                  ?.map(item => (
                    <Option key={item.id} value={item.id} title="">
                      {t('minute', {value: item.lengthMin})}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
            <GradientButton
              disabled={diamondBalance < diamondAmount}
              style={{width: 'fit-content', display: 'flex', justifyContent: 'center', margin: 0}}
              onClick={() => {
                form.submit();
              }}
            >
              {t('recharge')}
            </GradientButton>
          </div>
        </Form>
      </div>
    </>
  );
});

ExtendTimer.displayName = 'ExtendTimer';

export default memo(ExtendTimer);
